/**
 * O GDD do Mistik Island dentro do GG Pilot — para o time revisar o documento inteiro.
 *
 * <p><b>Cópia fiel, e não transcrição.</b> As quarenta e seis seções vêm como o Theo as
 * revisou, com tabelas e selos. Uma versão resumida mudaria o que está sendo revisado —
 * e revisão é justamente ler o que está escrito, não um retrato dele.
 *
 * <p><b>Por isso esta é a única tela que desenha HTML vindo do banco</b>, e por isso ele
 * passa pelo {@code DOMPurify} antes — a mesma higienização que o visor de Documentos já
 * faz com markdown. A regra do {@code TextoRico} (nada entra como HTML) continua valendo
 * para o que o time <i>escreve</i>: os comentários de revisão são texto puro.
 *
 * <p><b>Editar e comentar são coisas diferentes</b>, e a tela as separa: o lápis abre a
 * seção para corrigir o documento (gravação com trava de versão), e a conversa embaixo é
 * a revisão — o que precisa mudar, e por quê. Misturar as duas faria a discussão sumir
 * dentro do texto no dia em que alguém aceitasse a sugestão.
 */
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import DOMPurify from 'dompurify';

import { api, ErroApi } from '../../api/cliente';
import { TituloDaPagina } from '../../shell/TituloDaPagina';
import { Conversa } from '../Conversa';
import { CHAVE_ROADMAP, SLUG } from '../roadmapApi';
import type { Membro, Roadmap } from '../../api/tipos';
import * as gddApi from './gddApi';
import type { SecaoDoGdd } from './gddApi';
import './gdd.css';

export function Gdd() {
  const fila = useQueryClient();
  const documento = useQuery({
    queryKey: gddApi.chaveDoGdd,
    queryFn: gddApi.ler,
    staleTime: Infinity,
  });

  /** A seção aberta para edição — uma por vez, como o lápis de cada cabeçalho abre. */
  const [editando, setEditando] = useState<number | null>(null);

  /*
   * O time vem do roadmap, pela MESMA chave que a timeline usa: é a lista que a caixa de
   * comentário precisa para sugerir @handle, e ela quase sempre já está em cache quando
   * alguém chega aqui. Uma consulta própria só para os membros seria um segundo lugar
   * dizendo quem é do time.
   */
  const roadmap = useQuery<Roadmap>({
    queryKey: CHAVE_ROADMAP,
    queryFn: () => api(`/api/projetos/${SLUG}/roadmap`),
    staleTime: Infinity,
  });
  const membros = roadmap.data?.membros ?? [];

  const secoes = documento.data?.secoes ?? [];

  /**
   * O índice, agrupado como o documento original o agrupa.
   *
   * <p>Derivado da lista, e não uma segunda lista de grupos: o dia em que uma seção
   * mudar de grupo, o índice muda junto — dois lugares dizendo a mesma divisão é a
   * régua duplicada de sempre.
   */
  const grupos = useMemo(() => {
    const mapa = new Map<string, SecaoDoGdd[]>();
    for (const s of secoes) {
      const lista = mapa.get(s.grupo) ?? [];
      lista.push(s);
      mapa.set(s.grupo, lista);
    }
    return [...mapa.entries()];
  }, [secoes]);

  if (documento.error) {
    return (
      <Moldura>
        <p className="gdd-espera erro">
          {documento.error instanceof Error
            ? documento.error.message
            : 'Não foi possível abrir o GDD.'}
        </p>
      </Moldura>
    );
  }

  if (!documento.data) {
    return (
      <Moldura>
        <p className="gdd-espera">Abrindo o documento…</p>
      </Moldura>
    );
  }

  const { total, revisadas } = documento.data;
  const pct = total > 0 ? Math.round((revisadas / total) * 100) : 0;

  return (
    <Moldura>
      {/*
        A régua da revisão. O número vem do servidor — a tela não recontar é o que
        impede a barra e a lista de discordarem quando uma seção for marcada por outra
        pessoa e a lista ainda não tiver recarregado.
      */}
      <div className="gdd-regua">
        <span className="gdd-regua-rotulo">
          Revisão: <b>{revisadas}/{total}</b> seções <span className="gdd-pct">({pct}%)</span>
        </span>
        <span className="gdd-trilho">
          <i style={{ width: `${pct}%` }} />
        </span>
      </div>

      <div className="gdd-corpo">
        <nav className="gdd-indice" aria-label="Índice do documento">
          {grupos.map(([grupo, doGrupo]) => (
            <div key={grupo} className="gdd-indice-grupo">
              <b>{grupo}</b>
              <ul>
                {doGrupo.map((s) => (
                  <li key={s.id}>
                    <a href={`#${s.chave}`} className={s.revisada ? 'revisada' : undefined}>
                      <span className="gdd-n">{s.numero}</span>
                      <span>{s.titulo}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <main className="gdd-doc">
          {secoes.map((secao) => (
            <Secao
              key={secao.id}
              secao={secao}
              membros={membros}
              editando={editando === secao.id}
              aoEditar={() => setEditando(secao.id)}
              aoFechar={() => setEditando(null)}
              aoGravar={() => void fila.invalidateQueries({ queryKey: gddApi.chaveDoGdd })}
            />
          ))}
        </main>
      </div>
    </Moldura>
  );
}

function Secao({
  secao,
  membros,
  editando,
  aoEditar,
  aoFechar,
  aoGravar,
}: {
  secao: SecaoDoGdd;
  membros: Membro[];
  editando: boolean;
  aoEditar: () => void;
  aoFechar: () => void;
  aoGravar: () => void;
}) {
  const fila = useQueryClient();
  const [rascunho, setRascunho] = useState(secao.conteudo);
  const [erro, setErro] = useState('');

  /*
   * A higienização acontece aqui, e não no servidor: o que está guardado é o documento
   * como ele é — com as tabelas e os selos —, e é na hora de virar DOM que um
   * `<script>` faria estrago. Guardar já higienizado apagaria em silêncio o que um dia
   * for legítimo no documento.
   */
  const html = useMemo(() => DOMPurify.sanitize(secao.conteudo), [secao.conteudo]);

  const gravacao = useMutation({
    mutationFn: () => gddApi.salvarSecao(secao.id, rascunho, secao.versao),
    onSuccess: () => {
      setErro('');
      aoFechar();
      aoGravar();
    },
    onError: (e) =>
      setErro(
        e instanceof ErroApi && e.status === 409
          ? e.message
          : `Não consegui gravar: ${e instanceof Error ? e.message : 'erro desconhecido'}.`
      ),
  });

  const revisao = useMutation({
    mutationFn: (revisada: boolean) => gddApi.marcarRevisada(secao.id, revisada),
    onSuccess: () => void fila.invalidateQueries({ queryKey: gddApi.chaveDoGdd }),
  });

  return (
    <section id={secao.chave} className={`gdd-secao${secao.revisada ? ' revisada' : ''}`}>
      <header className="gdd-cabeca">
        <span className="gdd-num">{secao.numero}</span>
        <h2>{secao.titulo}</h2>
        <span className="gdd-acoes">
          {!editando && (
            <button
              type="button"
              className="gdd-bt"
              title="Editar esta seção"
              onClick={() => {
                /*
                 * O rascunho nasce do que está gravado AGORA. Sem isto, reabrir a edição
                 * depois de outra pessoa ter escrito traria de volta o texto que esta aba
                 * viu na primeira vez — e gravá-lo desfaria o dela sem ninguém notar,
                 * porque a versão que a tela carrega já teria sido atualizada.
                 */
                setRascunho(secao.conteudo);
                setErro('');
                aoEditar();
              }}
            >
              ✎ Editar
            </button>
          )}
          <label className="gdd-revisada" title="Marcar esta seção como revisada pelo time">
            <input
              type="checkbox"
              checked={secao.revisada}
              disabled={revisao.isPending}
              onChange={(e) => revisao.mutate(e.target.checked)}
            />
            revisada
          </label>
        </span>
      </header>

      {editando ? (
        <div className="gdd-edicao">
          {/*
            O texto do documento em HTML, como ele está guardado. É o formato em que ele
            chegou — com tabela e selo —, e um editor visual teria de reconstruí-lo a
            cada gravação; enquanto a revisão for de conteúdo, mexer na marcação é raro
            e explícito.
          */}
          <textarea
            className="gdd-campo"
            value={rascunho}
            spellCheck
            aria-label={`Conteúdo da seção ${secao.numero} ${secao.titulo}`}
            onChange={(e) => setRascunho(e.target.value)}
          />
          <div className="gdd-barra">
            <button
              type="button"
              className="gdd-bt primario"
              onClick={() => gravacao.mutate()}
              disabled={gravacao.isPending || rascunho.trim() === ''}
            >
              {gravacao.isPending ? 'Gravando…' : 'Salvar seção'}
            </button>
            <button
              type="button"
              className="gdd-bt"
              onClick={() => {
                setRascunho(secao.conteudo);
                setErro('');
                aoFechar();
              }}
            >
              Cancelar
            </button>
            {erro && <span className="gdd-erro">{erro}</span>}
            {!erro && secao.atualizadoPor && (
              <span className="gdd-meta">Última edição por {secao.atualizadoPor}.</span>
            )}
          </div>
        </div>
      ) : (
        /* Higienizado logo acima — ver o cabeçalho do arquivo. */
        <div className="gdd-conteudo" dangerouslySetInnerHTML={{ __html: html }} />
      )}

      {/*
        A revisão da seção: a MESMA conversa das frentes, com @menção que notifica,
        anexo e editar/apagar. Fica embaixo do texto e não dentro dele — o documento é
        o que se revisa, a conversa é a revisão, e misturá-las faria a discussão sumir
        no dia em que alguém aceitasse a sugestão e reescrevesse o trecho.

        Fechada por padrão: com quarenta e seis conversas abertas, o documento vira
        rodapé de si mesmo. O contador no resumo é o que chama quem precisa entrar.
      */}
      <details className="gdd-revisao">
        <summary>Revisão desta seção</summary>
        <Conversa dono={{ tipo: 'secao', id: secao.id }} membros={membros} />
      </details>
    </section>
  );
}

function Moldura({ children }: { children: ReactNode }) {
  return (
    <div className="gdd">
      <TituloDaPagina>
        O documento de design do Mistik Island, para o time revisar seção a seção — o
        texto de um lado, a conversa da revisão do outro.
      </TituloDaPagina>
      {children}
    </div>
  );
}
